import * as React from 'react'
import { withRouter, RouteComponentProps } from 'react-router'
import { ThemeProvider } from 'styled-components'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'
import { getCurrentUser, isAuthenticated, login } from 'web-fns/dist/auth'
import LoadablePage from 'web-makeup/dist/LoadablePage'
import LocaleChooser from 'web-makeup/dist/LocaleChooser'
import theme from 'web-makeup/dist/theme'
import { Mutation } from 'react-apollo'
import Component from '@reactions/component'
import gql from 'graphql-tag'
import Datadog from 'react-datadog'
import PayPage from 'web-ui/dist/order/views/pay-page/PayPage'

import { withFeatureFlags } from 'web-ui/dist/feature-flags/with-feature-flags'
import { initGA, setPageView, setGaUserId } from 'web-ui/dist/helpers/ga-tracking/ga-tracking'

import ApolloProvider from '../ApolloProvider'
import EnvironmentContext from '../EnvironmentContext'
import FeatureContext from '../FeatureContext'
import StockDataProvider from '../StockDataProvider'
import { UserProvider } from '../UserProvider'
import BrandDashboardOrdersNew from '../BrandDashboard/BrandDashboardOrdersNew/BrandDashboardOrdersNew'
import BrandDashboardTemplates from '../BrandDashboard/BrandDashboardTemplates/BrandDashboardTemplates'
import BrandDashboardWallets from '../BrandDashboard/BrandDashboardWallets/BrandDashboardWallets'
import BrandDashboardSettings from '../BrandDashboard/BrandDashboardSettings/BrandDashboardSettings'
import BrandDashboardTeam from '../BrandDashboard/BrandDashboardTeam/BrandDashboardTeam'
import BrandDashboardIntegrations from '../BrandDashboard/BrandDashboardIntegrations/BrandDashboardIntegrations'
import BrandDashboardSubBrands from '../BrandDashboard/BrandDashboardSubBrands/BrandDashboardSubBrands'
import BrandDashboardPayments from '../BrandDashboard/BrandDashboardPayments/BrandDashboardPayments'
import Profile from '../Profile/Profile'
import { getSearchToken as typesenseSearchToken } from '../../helpers/getSearchToken'

import { LoginJwtCookie } from '../../typings/types'
import { setAnalyticParams } from '../../helpers/gaTrack'

export const HomePage = LoadablePage({
  loader: () => import('../Home' /* webpackChunkName: "Home" */),
})

export const FourOhFour = LoadablePage({
  loader: () => import('../4xx' /* webpackChunkName: "4xx" */),
})

export const Purchase = LoadablePage({
  loader: () => import('../Purchase' /* webpackChunkName: "Purchase" */),
})

export const SelectBrands = LoadablePage({
  loader: () => import('../SelectBrands' /* webpackChunkName: "SelectBrands" */),
})

export const Create = LoadablePage({
  loader: () => import('../Create' /* webpackChunkName: "Create" */),
})

const VideoPerformance = LoadablePage({
  loader: () => import('../../widgets/VideoPerformance' /* webpackChunkName: "Performance" */),
})

export const LOGIN_WITH_COOKIE = gql`
  mutation LoginJwtCookie {
    loginJwtCookie {
      jwt
    }
  }
`
export const isProduction = window.env && window.env.ENVIRONMENT === 'production'

export const SetJWTIfLoggedInToVaas = ({
  component: Comp,
  isPubliclyAccessible = false,
  ...rest
}) => {
  const envContext = React.useContext(EnvironmentContext)

  const onErrorHandler = () => {
    if (!isPubliclyAccessible) {
      window.location.assign(envContext.GEN1_BASE_URL)
    }
  }

  return (
    <Route
      {...rest}
      render={props => {
        if (isAuthenticated()) {
          return <Comp {...props} />
        }

        return (
          <Mutation<LoginJwtCookie.Mutation, LoginJwtCookie.Variables>
            mutation={LOGIN_WITH_COOKIE}
            onError={onErrorHandler}
          >
            {(getJWT, { data, error, loading }) => {
              if (loading) {
                return null
              }

              if (isPubliclyAccessible && error) {
                return <Comp {...props} />
              }

              if (error) {
                return null
              }

              if (data && data.loginJwtCookie && data.loginJwtCookie.jwt) {
                login(data.loginJwtCookie.jwt)
                window.location.assign(props.match.url)
                return null
              }

              return <Component didMount={async () => getJWT()} />
            }}
          </Mutation>
        )
      }}
    />
  )
}

export const MainApp = withRouter(({ location }: RouteComponentProps) => {
  const [searchToken, setSearchToken] = React.useState<string>('')
  typesenseSearchToken().then(token => setSearchToken(token))
  const envContext = React.useContext(EnvironmentContext)
  const currentUser = getCurrentUser()

  React.useEffect(() => {
    if (window.env && window.env.GA_TRACKER_ID) {
      initGA(window.env.GA_TRACKER_ID)
      if (currentUser && currentUser.id) {
        setGaUserId(currentUser.id)
      }
      setPageView()
    }
  })

  React.useEffect(() => {
    setAnalyticParams(location.search)
  }, [])

  return (
    <Switch>
      <Route
        exact
        path="/"
        component={() => {
          window.location.assign(envContext.GEN1_BASE_URL)
          return null
        }}
      />
      <Route
        exact
        path="/brand-dashboard/"
        component={() => {
          window.location.assign(envContext.GEN1_BASE_URL)
          return null
        }}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/auth/handoff/brand-selection"
        to="/create"
        component={SelectBrands}
        searchToken={searchToken}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/:id"
        component={props => <BrandDashboardOrdersNew {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/profile"
        component={props => <Profile {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/orders/:id"
        component={props => <BrandDashboardTemplates {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/wallets/:id"
        component={props => <BrandDashboardWallets {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/settings/:id"
        component={props => <BrandDashboardSettings {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/team/:id"
        component={props => <BrandDashboardTeam {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/sub-brands/:id"
        component={props => <BrandDashboardSubBrands {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/integrations/:id"
        component={props => <BrandDashboardIntegrations {...props} searchToken={searchToken} />}
      />
      <SetJWTIfLoggedInToVaas
        exact
        path="/brand-dashboard/payments/:id"
        component={props => <BrandDashboardPayments {...props} searchToken={searchToken} />}
      />
      {/* This part is enabling antd modal styles */}
      <SetJWTIfLoggedInToVaas
        exact
        path="/newfoundation/pay/:id"
        component={props => <PayPage {...props} />}
      />
      <Route component={FourOhFour} />
    </Switch>
  )
})

interface AppProps {
  env: Window['env']
}

const App: React.FC<AppProps> = ({ env }) => {
  const currentUser = getCurrentUser()
  const experimentalFeatures = ['clickmap']
  const tracingOrigins = [
    /https?:\/.90seconds\.\s*(com|cc|team)/,
    /https?:\/\/[^\.]+\.90seconds\.\s*(com|cc|team)/,
  ]
  return (
    <EnvironmentContext.Provider value={env}>
      <FeatureContext.Provider value={env.FEATURES}>
        <UserProvider>
          <ThemeProvider theme={theme}>
            <LocaleChooser availableLocales={['en']}>
              {({ locale }) => (
                <StockDataProvider locale={locale}>
                  <ApolloProvider>
                    <>
                      <Router>
                        <Switch>
                          <SetJWTIfLoggedInToVaas
                            path="/widget/performance/:videoId/:organisationId?/:theme?"
                            component={VideoPerformance}
                          />
                          <Route
                            path="/"
                            render={props => (
                              <Datadog
                                applicationId={window.env.DATADOG_APP_ID}
                                clientToken={window.env.DATADOG_CLIENT_TOKEN}
                                service="brand"
                                sessionReplayRecording
                                enabled={window.env.ENVIRONMENT === 'production'}
                                user={{ id: currentUser ? currentUser.uuid : undefined }}
                                enableExperimentalFeatures={experimentalFeatures}
                                allowedTracingOrigins={tracingOrigins}
                              >
                                <MainApp {...props} />
                              </Datadog>
                            )}
                          />
                        </Switch>
                      </Router>
                    </>
                  </ApolloProvider>
                </StockDataProvider>
              )}
            </LocaleChooser>
          </ThemeProvider>
        </UserProvider>
      </FeatureContext.Provider>
    </EnvironmentContext.Provider>
  )
}

export default withFeatureFlags(App)



// WEBPACK FOOTER //
// ./src/components/App/App.tsx